// Minimap UI Component

import type { Zone } from 'shared';

export interface MinimapMarker {
  id: string;
  type: 'player' | 'party' | 'enemy' | 'npc' | 'vendor' | 'quest' | 'poi' | 'corpse';
  x: number;
  z: number;
  name?: string;
  color?: string;
  icon?: string;
}

export interface MinimapConfig {
  size: number;
  zoom: number;
  rotation: boolean;
  showCoordinates: boolean;
  showZoneName: boolean;
}

export class Minimap {
  private container: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: MinimapConfig;

  private playerX: number = 0;
  private playerZ: number = 0;
  private playerRotation: number = 0;
  private currentZone: Zone | null = null;
  private markers: MinimapMarker[] = [];
  private isExpanded: boolean = false;

  // Marker colors by type
  private markerColors: Record<string, string> = {
    player: '#4a90e2',
    party: '#27ae60',
    enemy: '#e74c3c',
    npc: '#f1c40f',
    vendor: '#9b59b6',
    quest: '#f39c12',
    poi: '#3498db',
    corpse: '#7f8c8d',
  };

  // Marker icons (for canvas drawing)
  private markerShapes: Record<string, string> = {
    player: 'arrow',
    party: 'circle',
    enemy: 'diamond',
    npc: 'circle',
    vendor: 'square',
    quest: 'exclamation',
    poi: 'star',
    corpse: 'x',
  };

  constructor(config?: Partial<MinimapConfig>) {
    this.config = {
      size: 180,
      zoom: 3,
      rotation: true,
      showCoordinates: true,
      showZoneName: true,
      ...config,
    };

    // Create container
    this.container = document.createElement('div');
    this.container.id = 'minimap';
    this.container.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 100;
      user-select: none;
    `;

    // Create canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.config.size;
    this.canvas.height = this.config.size;
    this.canvas.style.cssText = `
      border-radius: 50%;
      border: 3px solid #4a4a6a;
      box-shadow: 0 4px 15px rgba(0,0,0,0.4), inset 0 0 20px rgba(0,0,0,0.3);
      cursor: pointer;
    `;

    this.ctx = this.canvas.getContext('2d')!;

    // Zone name display
    const zoneName = document.createElement('div');
    zoneName.id = 'minimap-zone';
    zoneName.style.cssText = `
      text-align: center;
      color: #ffd700;
      font-size: 12px;
      font-weight: bold;
      text-shadow: 1px 1px 2px #000;
      margin-top: 5px;
      font-family: 'Segoe UI', Arial, sans-serif;
    `;

    // Coordinates display
    const coords = document.createElement('div');
    coords.id = 'minimap-coords';
    coords.style.cssText = `
      text-align: center;
      color: #aaa;
      font-size: 10px;
      font-family: 'Courier New', monospace;
      margin-top: 2px;
    `;

    // Zoom controls
    const controls = document.createElement('div');
    controls.style.cssText = `
      display: flex;
      justify-content: center;
      gap: 5px;
      margin-top: 5px;
    `;
    controls.innerHTML = `
      <button id="minimap-zoom-out" style="width: 24px; height: 24px; border-radius: 4px; border: 1px solid #4a4a6a; background: #1a1a2e; color: #fff; cursor: pointer;">-</button>
      <button id="minimap-zoom-in" style="width: 24px; height: 24px; border-radius: 4px; border: 1px solid #4a4a6a; background: #1a1a2e; color: #fff; cursor: pointer;">+</button>
      <button id="minimap-expand" style="width: 24px; height: 24px; border-radius: 4px; border: 1px solid #4a4a6a; background: #1a1a2e; color: #fff; cursor: pointer; font-size: 10px;">⛶</button>
    `;

    this.container.appendChild(this.canvas);
    this.container.appendChild(zoneName);
    this.container.appendChild(coords);
    this.container.appendChild(controls);
    document.body.appendChild(this.container);

    this.setupEventListeners();
    this.render();
  }

  private setupEventListeners(): void {
    // Zoom controls
    document.getElementById('minimap-zoom-in')?.addEventListener('click', () => {
      this.config.zoom = Math.min(10, this.config.zoom + 0.5);
      this.render();
    });

    document.getElementById('minimap-zoom-out')?.addEventListener('click', () => {
      this.config.zoom = Math.max(1, this.config.zoom - 0.5);
      this.render();
    });

    document.getElementById('minimap-expand')?.addEventListener('click', () => {
      this.toggleExpand();
    });

    // Click on minimap to expand
    this.canvas.addEventListener('click', () => {
      this.toggleExpand();
    });

    // M key to toggle map
    document.addEventListener('keydown', (e) => {
      if (e.key === 'm' || e.key === 'M') {
        if (!this.isInputFocused()) {
          this.toggleExpand();
        }
      }
    });
  }

  private isInputFocused(): boolean {
    const active = document.activeElement;
    return active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
  }

  private toggleExpand(): void {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.canvas.width = 400;
      this.canvas.height = 400;
      this.canvas.style.borderRadius = '8px';
      this.container.style.top = '50%';
      this.container.style.right = '50%';
      this.container.style.transform = 'translate(50%, -50%)';
    } else {
      this.canvas.width = this.config.size;
      this.canvas.height = this.config.size;
      this.canvas.style.borderRadius = '50%';
      this.container.style.top = '10px';
      this.container.style.right = '10px';
      this.container.style.transform = 'none';
    }

    this.render();
  }

  public updatePlayerPosition(x: number, z: number, rotation: number): void {
    this.playerX = x;
    this.playerZ = z;
    this.playerRotation = rotation;

    // Update coordinates display
    const coordsEl = document.getElementById('minimap-coords');
    if (coordsEl && this.config.showCoordinates) {
      coordsEl.textContent = `${Math.round(x)}, ${Math.round(z)}`;
    }
  }

  public setZone(zone: Zone | null): void {
    this.currentZone = zone;

    // Update zone name display
    const zoneEl = document.getElementById('minimap-zone');
    if (zoneEl && this.config.showZoneName) {
      zoneEl.textContent = zone?.name || 'Unknown';
    }
  }

  public setMarkers(markers: MinimapMarker[]): void {
    this.markers = markers;
  }

  public addMarker(marker: MinimapMarker): void {
    const existing = this.markers.findIndex(m => m.id === marker.id);
    if (existing >= 0) {
      this.markers[existing] = marker;
    } else {
      this.markers.push(marker);
    }
  }

  public removeMarker(id: string): void {
    this.markers = this.markers.filter(m => m.id !== id);
  }

  public render(): void {
    const size = this.isExpanded ? 400 : this.config.size;
    const center = size / 2;
    const zoom = this.isExpanded ? this.config.zoom * 0.5 : this.config.zoom;

    // Clear canvas
    this.ctx.clearRect(0, 0, size, size);

    // Draw background
    const gradient = this.ctx.createRadialGradient(center, center, 0, center, center, center);
    gradient.addColorStop(0, '#1a2a3a');
    gradient.addColorStop(1, '#0d1520');
    this.ctx.fillStyle = gradient;

    if (this.isExpanded) {
      this.ctx.fillRect(0, 0, size, size);
    } else {
      this.ctx.beginPath();
      this.ctx.arc(center, center, center - 2, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Draw grid
    this.drawGrid(center, zoom, size);

    // Draw zone boundaries if expanded
    if (this.isExpanded && this.currentZone) {
      this.drawZoneBounds(center, zoom);
    }

    // Draw markers
    this.drawMarkers(center, zoom, size);

    // Draw player
    this.drawPlayer(center);

    // Draw compass
    this.drawCompass(center, size);

    // Clip to circle if not expanded
    if (!this.isExpanded) {
      this.ctx.globalCompositeOperation = 'destination-in';
      this.ctx.beginPath();
      this.ctx.arc(center, center, center - 2, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.globalCompositeOperation = 'source-over';
    }
  }

  private drawGrid(center: number, zoom: number, size: number): void {
    this.ctx.strokeStyle = 'rgba(100, 150, 200, 0.15)';
    this.ctx.lineWidth = 1;

    const gridSize = 20 * zoom;
    const offsetX = (this.playerX * zoom) % gridSize;
    const offsetZ = (this.playerZ * zoom) % gridSize;

    // Vertical lines
    for (let x = -size; x < size * 2; x += gridSize) {
      const drawX = x - offsetX + center;
      this.ctx.beginPath();
      this.ctx.moveTo(drawX, 0);
      this.ctx.lineTo(drawX, size);
      this.ctx.stroke();
    }

    // Horizontal lines
    for (let z = -size; z < size * 2; z += gridSize) {
      const drawZ = z - offsetZ + center;
      this.ctx.beginPath();
      this.ctx.moveTo(0, drawZ);
      this.ctx.lineTo(size, drawZ);
      this.ctx.stroke();
    }
  }

  private drawZoneBounds(center: number, zoom: number): void {
    if (!this.currentZone) return;

    const bounds = this.currentZone.bounds;
    const minX = (bounds.minX - this.playerX) * zoom + center;
    const maxX = (bounds.maxX - this.playerX) * zoom + center;
    const minZ = (bounds.minZ - this.playerZ) * zoom + center;
    const maxZ = (bounds.maxZ - this.playerZ) * zoom + center;

    this.ctx.strokeStyle = 'rgba(255, 215, 0, 0.4)';
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([5, 5]);
    this.ctx.strokeRect(minX, minZ, maxX - minX, maxZ - minZ);
    this.ctx.setLineDash([]);
  }

  private drawMarkers(center: number, zoom: number, size: number): void {
    for (const marker of this.markers) {
      // Calculate position relative to player
      const relX = (marker.x - this.playerX) * zoom;
      const relZ = (marker.z - this.playerZ) * zoom;

      // Apply rotation if enabled
      let drawX: number, drawZ: number;
      if (this.config.rotation && !this.isExpanded) {
        const cos = Math.cos(-this.playerRotation);
        const sin = Math.sin(-this.playerRotation);
        drawX = relX * cos - relZ * sin + center;
        drawZ = relX * sin + relZ * cos + center;
      } else {
        drawX = relX + center;
        drawZ = relZ + center;
      }

      // Check if within map bounds
      const radius = this.isExpanded ? size / 2 : center - 5;
      const dist = Math.sqrt((drawX - center) ** 2 + (drawZ - center) ** 2);
      if (dist > radius) {
        // Draw at edge pointing to marker
        const angle = Math.atan2(drawZ - center, drawX - center);
        drawX = center + Math.cos(angle) * (radius - 5);
        drawZ = center + Math.sin(angle) * (radius - 5);
      }

      // Draw marker
      const color = marker.color || this.markerColors[marker.type] || '#fff';
      const markerSize = marker.type === 'player' ? 6 : 4;

      this.ctx.fillStyle = color;
      this.ctx.beginPath();

      switch (this.markerShapes[marker.type]) {
        case 'arrow':
          this.drawArrow(drawX, drawZ, markerSize, this.playerRotation);
          break;
        case 'diamond':
          this.drawDiamond(drawX, drawZ, markerSize);
          break;
        case 'square':
          this.ctx.fillRect(drawX - markerSize / 2, drawZ - markerSize / 2, markerSize, markerSize);
          break;
        case 'star':
          this.drawStar(drawX, drawZ, markerSize);
          break;
        case 'exclamation':
          this.drawExclamation(drawX, drawZ, markerSize);
          break;
        case 'x':
          this.drawX(drawX, drawZ, markerSize);
          break;
        default:
          this.ctx.arc(drawX, drawZ, markerSize / 2, 0, Math.PI * 2);
          this.ctx.fill();
      }
    }
  }

  private drawPlayer(center: number): void {
    this.ctx.fillStyle = '#4a90e2';
    this.ctx.strokeStyle = '#fff';
    this.ctx.lineWidth = 2;

    this.ctx.save();
    this.ctx.translate(center, center);
    if (!this.config.rotation || this.isExpanded) {
      this.ctx.rotate(this.playerRotation);
    }

    // Draw arrow shape
    this.ctx.beginPath();
    this.ctx.moveTo(0, -8);
    this.ctx.lineTo(6, 6);
    this.ctx.lineTo(0, 3);
    this.ctx.lineTo(-6, 6);
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.stroke();

    this.ctx.restore();
  }

  private drawCompass(center: number, size: number): void {
    const radius = this.isExpanded ? size / 2 - 20 : center - 15;

    // Calculate north position based on player rotation
    let northAngle = -Math.PI / 2; // North is up
    if (this.config.rotation && !this.isExpanded) {
      northAngle -= this.playerRotation;
    }

    const northX = center + Math.cos(northAngle) * radius;
    const northZ = center + Math.sin(northAngle) * radius;

    // Draw N
    this.ctx.fillStyle = '#e74c3c';
    this.ctx.font = 'bold 12px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('N', northX, northZ);

    // Draw other directions if expanded
    if (this.isExpanded) {
      this.ctx.fillStyle = '#888';
      const dirs = [
        { label: 'E', angle: 0 },
        { label: 'S', angle: Math.PI / 2 },
        { label: 'W', angle: Math.PI },
      ];

      for (const dir of dirs) {
        let angle = dir.angle - Math.PI / 2;
        if (this.config.rotation) {
          angle -= this.playerRotation;
        }
        const x = center + Math.cos(angle) * radius;
        const z = center + Math.sin(angle) * radius;
        this.ctx.fillText(dir.label, x, z);
      }
    }
  }

  private drawArrow(x: number, z: number, size: number, rotation: number): void {
    this.ctx.save();
    this.ctx.translate(x, z);
    this.ctx.rotate(rotation);
    this.ctx.beginPath();
    this.ctx.moveTo(0, -size);
    this.ctx.lineTo(size * 0.6, size * 0.6);
    this.ctx.lineTo(0, size * 0.3);
    this.ctx.lineTo(-size * 0.6, size * 0.6);
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.restore();
  }

  private drawDiamond(x: number, z: number, size: number): void {
    this.ctx.beginPath();
    this.ctx.moveTo(x, z - size);
    this.ctx.lineTo(x + size, z);
    this.ctx.lineTo(x, z + size);
    this.ctx.lineTo(x - size, z);
    this.ctx.closePath();
    this.ctx.fill();
  }

  private drawStar(x: number, z: number, size: number): void {
    const spikes = 5;
    const outerRadius = size;
    const innerRadius = size / 2;

    this.ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (i * Math.PI) / spikes - Math.PI / 2;
      const px = x + Math.cos(angle) * radius;
      const pz = z + Math.sin(angle) * radius;
      if (i === 0) {
        this.ctx.moveTo(px, pz);
      } else {
        this.ctx.lineTo(px, pz);
      }
    }
    this.ctx.closePath();
    this.ctx.fill();
  }

  private drawExclamation(x: number, z: number, size: number): void {
    this.ctx.fillRect(x - size / 4, z - size, size / 2, size * 1.2);
    this.ctx.beginPath();
    this.ctx.arc(x, z + size * 0.6, size / 3, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private drawX(x: number, z: number, size: number): void {
    this.ctx.strokeStyle = this.ctx.fillStyle as string;
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(x - size, z - size);
    this.ctx.lineTo(x + size, z + size);
    this.ctx.moveTo(x + size, z - size);
    this.ctx.lineTo(x - size, z + size);
    this.ctx.stroke();
  }

  public dispose(): void {
    this.container.remove();
  }
}
